// src/pages/InvitePage.js

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const getOperatingSystem = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  if (/android/i.test(userAgent)) {
    return 'Android';
  }
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'iOS';
  }
  return 'Unknown';
};

const PLAY_STORE_LINK = 'https://play.google.com/store/apps/details?id=com.tocxi.app';
const APP_STORE_LINK = 'https://apps.apple.com/app/your-app-id'; // Replace this

const InvitePage = () => {
  const { referralCode } = useParams();
  const [operatingSystem, setOperatingSystem] = useState('Unknown');
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    setOperatingSystem(getOperatingSystem());
  }, []);

  const handleCopyCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode)
        .then(() => {
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2500);
        })
        .catch(err => {
          console.error('Copy failed:', err);
          alert('Could not copy code. Please copy manually.');
        });
    }
  };

  const renderDownloadButton = () => {
    const googleBadgeSrc = "/icons/play1.png";
    const appleBadgeSrc = "/images/app-store-badge-official.svg";

    const badgeStyle = { ...styles.badgeImage, maxWidth: '180px' }; // Adjusted for mobile

    if (operatingSystem === 'Android') {
      return (
        <a href={PLAY_STORE_LINK} target="_blank" rel="noopener noreferrer" style={styles.badgeLink}>
          <img src={googleBadgeSrc} alt="Get it on Google Play" style={badgeStyle} />
        </a>
      );
    } else if (operatingSystem === 'iOS') {
      return (
        <a href={APP_STORE_LINK} target="_blank" rel="noopener noreferrer" style={styles.badgeLink}>
          <img src={appleBadgeSrc} alt="Download on the App Store" style={badgeStyle} />
        </a>
      );
    } else {
      return (
        <a href={PLAY_STORE_LINK} target="_blank" rel="noopener noreferrer" style={styles.badgeLink}>
          <img src={googleBadgeSrc} alt="Get it on Google Play" style={badgeStyle} />
        </a>
      );
    }
  };

  return (
    <div style={styles.pageContainer}>
      <div style={styles.card}>
        <img src="/icons/icon.png" alt="Tocxi Logo" style={styles.logo} />

        <h1 style={styles.title}>You're Invited to Tocxi!</h1>
        <p style={styles.subtitle}>
          Join using the code below and get <span style={styles.benefitText}>₹50 OFF</span> your first ride! 🚖
        </p>

        <p style={styles.referralLabel}>Your Invite Code:</p>
        <div style={styles.referralContainer}>
          <span style={styles.referralCodeText}>{referralCode || 'N/A'}</span>
          <button
            onClick={handleCopyCode}
            style={isCopied ? { ...styles.copyButton, ...styles.copyButtonCopied } : styles.copyButton}
            disabled={isCopied}
          >
            {isCopied ? '✓ Copied!' : 'Copy'}
          </button>
        </div>

        <hr style={styles.divider} />

        <p style={styles.downloadLabel}>Download the App:</p>
        <div style={styles.downloadSection}>
          {renderDownloadButton()}
        </div>

        <p style={styles.footerText}>
          Already have the app? Just enter the code <strong style={styles.footerCode}>{referralCode}</strong> during signup or in the referrals section.
        </p>
      </div>
    </div>
  );
};

export default InvitePage;

const styles = {
  pageContainer: {
    minHeight: '100vh',
    background: 'linear-gradient(145deg, #EFF6FF 0%, #F9FAFB 100%)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '15px',
    fontFamily: 'Inter, sans-serif',
  },
  card: {
    backgroundColor: '#fff',
    padding: '25px 20px',
    borderRadius: 20,
    boxShadow: '0px 12px 30px rgba(42, 92, 255, 0.10)',
    textAlign: 'center',
    maxWidth: '420px',
    width: '100%',
    border: '1px solid #E5E7EB',
  },
  logo: {
    width: 70,
    height: 'auto',
    marginBottom: 20,
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: 10,
  },
  subtitle: {
    fontSize: '15px',
    color: '#4B5563',
    marginBottom: 20,
    lineHeight: 1.5,
  },
  benefitText: {
    fontWeight: '700',
    color: '#2563EB',
  },
  referralLabel: {
    fontSize: '12px',
    color: '#6B7280',
    marginBottom: 8,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  referralContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    borderRadius: 10,
    padding: '8px 12px',
    marginBottom: 20,
    justifyContent: 'space-between',
    border: '1px solid #E5E7EB',
  },
  referralCodeText: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1F2937',
    fontFamily: '"Source Code Pro", monospace',
    marginRight: 10,
  },
  copyButton: {
    backgroundColor: '#3B82F6',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '8px 12px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  copyButtonCopied: {
    backgroundColor: '#10B981',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #E5E7EB',
    margin: '20px 0',
  },
  downloadLabel: {
    fontSize: '13px',
    color: '#374151',
    marginBottom: 15,
  },
  downloadSection: {
    marginBottom: 20,
  },
  badgeLink: {
    display: 'inline-block',
    margin: '5px',
  },
  badgeImage: {
    height: '50px',
    width: 'auto',
  },
  footerText: {
    marginTop: 20,
    fontSize: '12px',
    color: '#6B7280',
    lineHeight: 1.5,
  },
  footerCode: {
    color: '#1F2937',
    backgroundColor: '#E5E7EB',
    padding: '2px 5px',
    borderRadius: 4,
    fontFamily: '"Source Code Pro", monospace',
  },
};
